<template>
	<div>
		<div class="block w-full mb-12" v-if="selectedProviderId === ''">
      <h4 class="text-grey mb-4">{{ $t('providerManagement.popups.providerSearch.title') }}</h4>

      <div class="grid grid-cols-1 gap-4 w-full">
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.providerList') }}
					</div>
					<div class="inputField flex-grow" >
            <Select :searchable="true"
                    :selected="selectedProviderId"
                    :options="foundExistingCompaniesList"
                    mode="single"
                    @selectChanged="selectProvider"
                    @input="searchForExistingCompanies"/>
          </div>
				</div>
			</div>

		</div>
		<div class="block w-full">

			<div class="grid grid-cols-2 gap-4 w-full">
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.name') }} *
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.name.$errors.length }">
							<input type="text" v-model="popupData.name" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" id="name" /><br/>
							<div class="input-errors" v-for="error of v$.name.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div><!-- SPACING --></div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.street') }} *
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.street.$errors.length }">
							<input type="text" v-model="popupData.street" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" id="street" /><br/>
							<div class="input-errors" v-for="error of v$.street.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.houseNumber') }} *
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.streetnumber.$errors.length }">
							<input type="text" v-model="popupData.streetnumber" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" id="streetnumber" /><br/>
							<div class="input-errors" v-for="error of v$.streetnumber.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.postalCode') }} *
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.zip.$errors.length }">
							<input type="text" v-model="popupData.zip" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" id="zip" /><br/>
							<div class="input-errors" v-for="error of v$.zip.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.city') }} *
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.city.$errors.length }">
							<input type="text" v-model="popupData.city" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" id="city" /><br/>
							<div class="input-errors" v-for="error of v$.city.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.phonePrefix') }} {{ !NullOrUndefinedHelper(popupData.phoneNumber) ? '*' : '' }}
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.dialCode.$errors.length }">
							<Select :options="countryDialCodes"
								:selected="selectedDialCode"
								class="w-full"
								mode="single"
								@selectChanged="selectedDialCodeChanged"
								:disabled="popupData.hasAdmin || isSelectedProvider"
								id="dialCode"
							/>
							<div class="input-errors" v-for="error of v$.dialCode.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.phoneNumber') }}
					</div>
					<div class="inputField flex-grow" >
						<div :class="{ error: v$.phoneNumber.$errors.length }">
							<input type="text" v-model="popupData.phoneNumber" class="w-full" :disabled="popupData.hasAdmin || isSelectedProvider" placeholder="17012345678" id="phoneNumber" /><br/>
							<div class="input-errors" v-for="error of v$.phoneNumber.$errors" :key="error.$uid">
								<div class="error-msg">{{ error.$message }}</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-1 gap-4 w-full mt-4">
				<div>
					<div class="inputTitle">
						{{ $t('providerManagement.popups.providerSearch.information') }}
					</div>
					<div class="inputField flex-grow" >
						<textarea rows="3" type="text" v-model="popupData.info" :disabled="popupData.hasAdmin || isSelectedProvider" class="w-full"></textarea><br/>
					</div>
				</div>
			</div>

		</div>
	</div>
</template>

<script setup>
import {computed, defineProps, onMounted, ref, toRef} from "vue";
import {helpers, required, numeric, requiredIf} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import {useI18n} from "vue-i18n";
import {ProviderService} from "@/utilities/services/entities/ProviderService";
import {countryDialCodes} from "@/AppConfig";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import {Provider} from "@/models/entities/providers/Provider";
import {useSystemStore} from "@/stores/system/SystemStore";
import {ErrorLocationType} from "@/utilities/system/enums/Enums";
import Select from "@/components/system/inputs/Select";

const systemStore = useSystemStore();
const { t } = useI18n();
const providerService = new ProviderService();
const isSelectedProvider = ref(false);

const props = defineProps({
	data: Object
})

const popupData = toRef(props, 'data');
const selectedProviderId = ref("");
const foundExistingCompaniesList = ref([]);

const selectedDialCode = computed(()=>{
  return countryDialCodes.find(cc => cc.dialCode === popupData.value.dialCode)?.id ?? "";
})
const selectedDialCodeChanged = (id)=>{
  popupData.value.dialCode = countryDialCodes.find(cc => cc.id === id)?.dialCode ?? "";
}

const selectProvider = async (id)=>{
	// set fields to readonly
	isSelectedProvider.value = true;

  // if deselect/'X'/clearing selection
  if(NullOrUndefinedHelper(id)) {
    // reset all inputs via the binding to entered data from previous edit view
    const resetObj = new Provider();
    for (const keyValueArray of Object.entries(resetObj)){
      popupData.value[keyValueArray[0]] = keyValueArray[1];
    }

    isSelectedProvider.value = false;

    return;
  }

  // get company from currently found list and set input fields via setting the props of popupData via reflection
  const foundCompany = foundExistingCompaniesList.value.find(p => p.id === id);

  if(foundCompany === undefined) return;

  // check if company has admin
  const hasAdminResp = await providerService.getProviderHasUserWithRole(id, "Admin", ErrorLocationType.popup);
  // if call fails silently fail... sad but true (\m/,) for the moment
  if(!hasAdminResp) return;

  foundCompany.hasAdmin = hasAdminResp.isExist;

  // set popupData for every property used, since vue-2-way-binding (v-model) doesn't work on parent object (popupData)
  for (const keyValueArray of Object.entries(foundCompany)){
    if(keyValueArray[0] === "info") return;
    popupData.value[keyValueArray[0]] = keyValueArray[1];
  }
};

const searchForExistingCompanies = async ({target})=>{
  if(NullOrUndefinedHelper(target.value)) return;

  // CAUTION does not check for errors for uninterupted inputs & 'fire and forget'-workflow
  const resp = await providerService.getProvidersByName(target.value, ErrorLocationType.popup);

  foundExistingCompaniesList.value = resp.companiesInfo;
}

onMounted(async () => {
  systemStore.modal.validator = v$;

  if (!NullOrUndefinedHelper(popupData.value.id)) {
    // check if company has admin
    const hasAdminResp = await providerService.getProviderHasUserWithRole(popupData.value.id, "Admin", ErrorLocationType.popup);
    // if call fails silently fail... sad but true (\m/,) for the moment
    if (!hasAdminResp) return;

    popupData.value.hasAdmin = hasAdminResp.isExist;

    selectedProviderId.value = popupData.value.id;
  }
})


// Validation

const rules = computed(() => {
	return {
		name: { required: helpers.withMessage(t('validators.required'), required) },
		streetnumber: { required: helpers.withMessage(t('validators.required'), required) },
		city: { required: helpers.withMessage(t('validators.required'), required) },
		street: { required: helpers.withMessage(t('validators.required'), required) },
		phoneNumber: {
			numeric: helpers.withMessage(t('validators.numeric'), numeric) ,
		},
		dialCode: { requiredIf: helpers.withMessage(t('validators.required'), requiredIf(()=> !NullOrUndefinedHelper(popupData.value.phoneNumber))) },
		zip: {
			required: helpers.withMessage(t('validators.required'), required),
			numeric: helpers.withMessage(t('validators.numeric'), numeric) ,
		},
	}
})

const v$ = ref(useVuelidate(rules, popupData))
</script>

<style scoped>

</style>
